import React, { useContext, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { AdminUserOutDTO, CompanyUserTypeEnum, LovSecurityRolesDTO } from '@api/ringil-api.v2';
import { isNotNullOrUndefined } from '@utils/utils';
import { Button } from 'primereact/button';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { NoWrapText } from '@components/styles.tsx';
import { EditUserSidebar } from '@app/pages/customer/CompanyProfile/sidebar/EditUserSidebar.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilDataTable, { ColumnConfig } from '@components/RingilDataTable/RingilDataTable.tsx';
import SvgUser from '@icons/user-filled.svg?react';

interface ComponentProps {
  companyId: string;
}

const AdminCompanyUsers: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const { adminGetUsers, adminCreateUserInAuth0, adminGetSecurityRoles, adminMoveLogsteoUserToCompanyUser } = useContext(ApiContext);
  const { showToastMessage } = useContext(ToastContext);
  const [showPermisionForUser, setShowPermisionForUser] = useState<string>();
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  const markUserAsDriver = (userId: string) => {
    adminMoveLogsteoUserToCompanyUser({ logsteoUserId: userId, companyUserType: CompanyUserTypeEnum.DRIVER }, () => {
      showToastMessage(tr('generic.done', 'Done'), tr('adminCompany.markedAsDriver', 'User marked as driver'), 'success');
    });
  };

  const renderActions = (row: AdminUserOutDTO) => {
    return (
      <NoWrapText>
        <Button
          label={tr('adminCompany.createInAuth0', 'Create in Auth0')}
          onClick={() => {
            adminCreateUserInAuth0({ userId: row.id }, () => {
              showToastMessage(tr('generic.done', 'Done'), tr('adminCompany.userCreatedInAuth0', 'User created in Auth0'), 'success');
            });
          }}
        />
        <Button
          label={tr('adminCompany.managePermission', 'Manage permission')}
          onClick={() => {
            setShowPermisionForUser(row.id);
          }}
        />
        <Button
          label={tr('adminCompany.markAsDriver', 'Mark as driver')}
          onClick={() => {
            markUserAsDriver(row.id);
          }}
        />
        <a href={`/?runAs=${row.id}`} target={'_blank'}>
          {tr('adminCompany.loginAsUser', 'Login as user')}
        </a>
      </NoWrapText>
    );
  };

  const columnConfig: ColumnConfig<AdminUserOutDTO>[] = [
    { id: 'email', field: 'email', header: 'EditUserSidebar.email', headerDefault: 'Email', width: '25rem', verticalAlign: 'middle', render: row => <span>{row.email}</span> },
    {
      id: 'firstName',
      field: 'firstName',
      header: 'EditUserSidebar.firstName',
      headerDefault: 'First name',
      width: '10rem',
      verticalAlign: 'middle',
      render: row => <span>{row.firstName}</span>,
    },
    {
      id: 'lastName',
      field: 'lastName',
      header: 'EditUserSidebar.surname',
      headerDefault: 'Surname',
      width: '10rem',
      verticalAlign: 'middle',
      render: row => <span>{row.lastName}</span>,
    },
    {
      id: 'companyName',
      field: 'companyName',
      header: 'adminCompanyDetail.companyName',
      headerDefault: 'Company name',
      width: '10rem',
      verticalAlign: 'middle',
      render: row => <span>{row.companyName}</span>,
    },
    { id: 'actions', width: '10rem', verticalAlign: 'middle', render: row => renderActions(row) },
  ];

  return (
    <SectionPanel
      icon={<SvgUser className={'w-6 h-6'} />}
      iconColor={'var(--content-tertiary)'}
      heading={tr('adminCompanyDetail.companyUsers', 'Company users')}
      headerRight={<Button label={tr('adminCompanyDetail.addUser', 'Add user')} onClick={() => setShowPermisionForUser('_NEW_')} />}
      dataTestId={'admin-company-section-users'}>
      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig as ColumnConfig<unknown>[]}
        filterName={'admin_company_users'}
        loadDataFromApi={(filter, pageable, onSuccess) =>
          adminGetUsers({ pageSize: pageable.pageSize, pageNumber: pageable.pageNumber }, { companyId }, d =>
            onSuccess({ data: d.data, total: d.total, page: d.page, size: d.size }),
          )
        }
      />

      <EditUserSidebar
        visible={isNotNullOrUndefined(showPermisionForUser)}
        onHide={() => {
          setShowPermisionForUser(undefined);
        }}
        onComplete={() => {
          setShowPermisionForUser(undefined);
          tableRef.current?.reloadData();
        }}
        userId={showPermisionForUser === '_NEW_' ? undefined : showPermisionForUser}
        companyId={companyId}
        loadSecurityRoles={(onSuccess: (data: LovSecurityRolesDTO[]) => void) =>
          adminGetSecurityRoles(data =>
            onSuccess(
              data.map(it => ({
                id: it.id,
                name: it.name,
                translateKey: it.translateKey,
                descriptionKey: it.descriptionKey,
                isPrivate: it.isPrivate,
                isOnlyInternal: it.isOnlyInternal,
                companyModules: it.companyModules,
              })),
            ),
          )
        }
        isAdmin={true}
      />
    </SectionPanel>
  );
};

export default AdminCompanyUsers;
